// ============================================================================
// Error Boundary - EduPath Africa
// Catches React render errors, chunk load failures, and provides recovery
// ============================================================================

import React, { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home, ArrowLeft, Bug } from 'lucide-react';
import { getDashboardForRole } from '@/lib/roleConfig';
import { useAuthStore } from '@/stores/authStore';

// ============================================================================
// Types
// ============================================================================

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  isChunkError: boolean;
}

// ============================================================================
// Chunk Load Error Detection
// ============================================================================

function isChunkLoadError(error: Error): boolean {
  const message = error.message?.toLowerCase() || '';
  const name = error.name?.toLowerCase() || '';
  return (
    message.includes('loading chunk') ||
    message.includes('loading css chunk') ||
    message.includes('dynamically imported module') ||
    message.includes('failed to fetch') ||
    name.includes('chunkloaderror')
  );
}

// ============================================================================
// Error Boundary Component (Class-based — required by React)
// ============================================================================

class ErrorBoundaryInner extends Component<
  ErrorBoundaryProps & { userRole?: string },
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps & { userRole?: string }) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      isChunkError: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
      isChunkError: isChunkLoadError(error),
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });

    // Log to console in development
    if (import.meta.env.DEV) {
      console.error('[ErrorBoundary] Caught error:', error);
      console.error('[ErrorBoundary] Component stack:', errorInfo.componentStack);
    }

    // Auto-retry chunk load errors once (likely a deployment cache issue)
    if (isChunkLoadError(error)) {
      const retryKey = 'edupath-chunk-retry';
      const lastRetry = sessionStorage.getItem(retryKey);
      const now = Date.now();

      // Only auto-retry if we haven't retried in the last 30 seconds
      if (!lastRetry || now - parseInt(lastRetry, 10) > 30000) {
        sessionStorage.setItem(retryKey, String(now));
        window.location.reload();
        return;
      }
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    const dashboard = getDashboardForRole(this.props.userRole);
    window.location.href = dashboard;
  };

  handleGoBack = () => {
    window.history.back();
  };

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      isChunkError: false,
    });
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    // Use custom fallback if provided
    if (this.props.fallback) {
      return this.props.fallback;
    }

    const { error, isChunkError } = this.state;

    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-primary-50 p-6">
        <div className="w-full max-w-lg">
          {/* Error Icon */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
              {isChunkError ? (
                <RefreshCw className="w-10 h-10 text-amber-600" />
              ) : (
                <AlertTriangle className="w-10 h-10 text-red-600" />
              )}
            </div>

            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {isChunkError ? 'Update Available' : 'Something went wrong'}
            </h1>

            <p className="text-gray-600 max-w-md mx-auto">
              {isChunkError
                ? 'EduPath has been updated. Please reload to get the latest version.'
                : 'An unexpected error occurred. You can try reloading the page or going back to your dashboard.'}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="space-y-3">
              {/* Primary: Reload */}
              <button
                onClick={this.handleReload}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 transition-colors"
              >
                <RefreshCw className="w-5 h-5" />
                {isChunkError ? 'Reload Page' : 'Try Again'}
              </button>

              {/* Secondary: Go Home */}
              <button
                onClick={this.handleGoHome}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Home className="w-5 h-5" />
                Go to Dashboard
              </button>

              {/* Tertiary: Go Back */}
              <button
                onClick={this.handleGoBack}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 text-gray-500 hover:text-gray-700 transition-colors text-sm"
              >
                <ArrowLeft className="w-4 h-4" />
                Go Back
              </button>
            </div>

            {/* Dev-only: Error Details */}
            {import.meta.env.DEV && error && (
              <details className="mt-6 border-t border-gray-100 pt-4">
                <summary className="flex items-center gap-2 text-sm text-gray-500 cursor-pointer hover:text-gray-700">
                  <Bug className="w-4 h-4" />
                  Error Details (dev only)
                </summary>
                <div className="mt-3 p-3 bg-gray-50 rounded-lg overflow-auto max-h-48">
                  <p className="text-xs font-mono text-red-600 break-all">
                    {error.name}: {error.message}
                  </p>
                  {this.state.errorInfo?.componentStack && (
                    <pre className="mt-2 text-xs font-mono text-gray-500 whitespace-pre-wrap">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  )}
                </div>
              </details>
            )}
          </div>

          {/* Support Link */}
          <p className="mt-6 text-center text-gray-500 text-sm">
            Need help?{' '}
            <a href="mailto:hello@edupath.africa" className="text-primary-600 hover:text-primary-700">
              hello@edupath.africa
            </a>
          </p>
        </div>
      </div>
    );
  }
}

// ============================================================================
// Wrapper to inject Zustand state into class component
// ============================================================================

const ErrorBoundary: React.FC<ErrorBoundaryProps> = (props) => {
  const user = useAuthStore((state) => state.user);
  return <ErrorBoundaryInner {...props} userRole={user?.role} />;
};

export default ErrorBoundary;
